// Thin REST client for the HTTP surface in SPEC.md §2. Same-origin by
// default (works with the vite dev/preview proxy and with a Cloudflare
// Pages + Worker deploy that fronts both under one domain); overridable via
// VITE_API_BASE for a split-domain deploy (client on Pages, API on a
// separate Worker route).
import type { ApiErrorBody, CreateRoomBody, JoinRoomBody, PublicRoomView } from './wire';

export interface LeaderboardRow {
  room_code: string;
  /** Host/team display name, best-effort (see server/db/migrations/
   * 0005_leaderboard_best_round.sql) -- null once a room row has been
   * reaped. Display code should fall back to room_code when this is null. */
  team_name: string | null;
  /** Best SINGLE completed round's total, not a lifetime sum across every
   * round ever played under this room_code. */
  team_total: number;
  ended_at: string;
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network error. Check your connection');
  }
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const msg = (parsed as ApiErrorBody)?.error ?? `request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return parsed as T;
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`);
  } catch {
    throw new ApiError(0, 'network error. Check your connection');
  }
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const msg = (parsed as ApiErrorBody)?.error ?? `request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return parsed as T;
}

export function createRoom(hostName: string): Promise<CreateRoomBody> {
  return postJson<CreateRoomBody>('/api/rooms', { host_name: hostName });
}

export function joinRoom(code: string, name: string): Promise<JoinRoomBody> {
  return postJson<JoinRoomBody>(`/api/rooms/${encodeURIComponent(code)}/join`, { name });
}

export function getRoomView(code: string): Promise<PublicRoomView> {
  return getJson<PublicRoomView>(`/api/rooms/${encodeURIComponent(code)}`);
}

/** SPEC.md §4/§5 "leaderboard (top team totals)" -- see the server-side bug
 * fix note in server/src/roomManager.ts (getLeaderboard was implemented on
 * every Store backend but had no HTTP route until this component). */
export function getLeaderboard(limit = 10): Promise<{ rows: LeaderboardRow[] }> {
  return getJson<{ rows: LeaderboardRow[] }>(`/api/leaderboard?limit=${limit}`);
}

/** Builds the /ws/{code}?token=... URL, same-origin unless VITE_WS_BASE is
 * set (split-domain deploy). Derives ws(s):// from the page's protocol when
 * no explicit base is configured. `debug` appends `&debug=1` -- the ONLY
 * thing that turns server-side turn-intent tracing on for this room (see
 * server/src/node/wsServer.ts onConnection) -- omitted entirely (not even
 * `debug=0`) when false, so an untraced session's WS URL is byte-identical
 * to before this feature existed. */
export function wsUrl(code: string, token: string, debug = false): string {
  const suffix = debug ? '&debug=1' : '';
  const explicit = import.meta.env.VITE_WS_BASE as string | undefined;
  if (explicit) {
    return `${explicit.replace(/\/$/, '')}/ws/${encodeURIComponent(code)}?token=${encodeURIComponent(token)}${suffix}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/${encodeURIComponent(code)}?token=${encodeURIComponent(token)}${suffix}`;
}

/** TURN-INTENT TRACE: POSTs one marker's merged trace window. See
 * game/trace.ts's `armMissMarker` (the caller) and
 * server/src/node/httpServer.ts's `/api/rooms/:code/trace` route (the
 * handler) for the full contract. */
export function postTrace(
  code: string,
  body: { seat: 1 | 2; markerT: number; entries: unknown[] },
): Promise<{ ok: boolean; file: string; entryCount: number }> {
  return postJson(`/api/rooms/${encodeURIComponent(code)}/trace`, body);
}
